import React, { useState } from "react";
import Modal from "@mui/material/Modal";
import Tooltip from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";

import LoadingOverlay from "../overlays/LoadingOverlay";
import { shareYourFeedback } from "../../api/shareYourFeedback";
import {
  commonSuccessToastMessage,
  feedbackSubmittedSuccessfully,
  commonErrorToastMessage
} from "../toastify/AllToastifyErrors";

const ShareYourFeedbackModal = ({ open, onClose, onSubmit }) => {
  // Add a new state variable to track the loading state
  const [isLoading, setIsLoading] = React.useState(false);

  // State variable to track the selected star count
  const [selectedStars, setSelectedStars] = useState(0);

  // State variable to track the rating error state
  const [showRatingError, setShowRatingError] = useState(false);

  // State variable to track the error state for the textarea
  const [showTextareaError, setShowTextareaError] = useState(false);

  // State variable to store the textarea input
  const [feedbackText, setFeedbackText] = useState("");

  const [errorMessage, setErrorMessage] = useState("");

  // detect URLs/domains (http(s), //, www, domain.tld, IP)
  const containsUrl = (text) => {
    if (!text) return false;
    const s = String(text).trim();
    const urlRegex = /(?:https?:\/\/|\/\/|www\.|[A-Za-z0-9-]{1,63}\.[A-Za-z]{2,24}|\b\d{1,3}(?:\.\d{1,3}){3}\b|http%3a|https%3a)/i;
   return urlRegex.test(s);
 };

function containsUrlOrHtmlTag(inputText) {
  // Regex to detect URLs (http, https, www)
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/i;

  // Regex to detect HTML tags like <a>, <div>, <img>, etc.
  const htmlTagRegex = /<[^>]*>/i;

  if (urlRegex.test(inputText)) {
    return "Input contains a URL.";
  } else if (htmlTagRegex.test(inputText)) {
    return "Input contains HTML tags.";
  } else {
    return "Input is clean (no URLs or HTML tags found).";
  }
}

  // Function to handle typing in the textarea and update the state
  const handleTextareaChange = (event) => {
    const newText = event.target.value;
    setFeedbackText(newText);
    if (newText.trim() !== "") {
      setShowTextareaError(false);
    }
    if (containsUrl(newText)) {
      setErrorMessage("Message must not contain links or domain names for security reasons.");
    } else {
      setErrorMessage("");
    }

    // if (containsUrlOrHtmlTag(newText)) {
    //   setErrorMessage("Message must not contain links or domain names for security reasons.");
    // } else {
    //   setErrorMessage("");
    // }
  };

  // Function to handle clicking on a star
  const handleStarClick = (starCount) => {
    setSelectedStars(starCount);
    setShowRatingError(false);
  };

  // Function to handle the "Close" button click
  const handleModalClose = () => {
    setSelectedStars(0);
    setShowTextareaError(false);
    setShowRatingError(false);
    onClose(); // Close the modal without processing the input
  };

  // Function to handle the form submission
  const handleFeedbackSubmit = async () => {
    if (selectedStars === 0) {
      setShowRatingError(true);
      return;
    }
    setShowRatingError(false);

    if (feedbackText.trim() === "") {
      setShowTextareaError(true);
      return;
    }
    setShowTextareaError(false);

    try {
      const userId = localStorage.getItem("Id");

      const loginToken = localStorage.getItem("token");
      const response = await shareYourFeedback(
        userId,
        selectedStars,
        feedbackText
      );

      commonSuccessToastMessage(
        "Thanks for your feedback! Your suggestions are integral to our progress."
      );

      setFeedbackText("");
      setSelectedStars(0);

      // Close the modal
      onClose();
    } catch (error) {
      if(error.response.status === 403){
        commonErrorToastMessage("Your session has expired please log in again.");
        setIsLoading(false);
        onClose();
      }else{ 
      console.error("Error : ", error);
      onClose();
      }
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="youtube-modal-title"
      // Apply the style to the modal container
      className="flex place-content-center items-center fixed"
      // Disable clicking on the backdrop and Escape key
      BackdropProps={{
        style: { pointerEvents: "none" },
      }}
      disableEscapeKeyDown
    >
      <div className="bg-white shadow-lg text-[#212529] p-6 rounded-lg   animate-fade-slide-down w-max">
        <div className="flex place-content-between items-center border-b pb-5">
          <h2 className=" text-xl font-semibold ">Share your feedback.</h2>
          <Tooltip
            title="Close"
            arrow
            placement="top"
            TransitionComponent={Zoom}
            TransitionProps={{ timeout: 300 }}
          >
            <button
              className="text-[#6c757d] text-lg hover:bg-[#edeeef] px-2 rounded-full "
              onClick={handleModalClose}
            >
              <i className="fa-solid fa-xmark"></i>{" "}
            </button>
          </Tooltip>
        </div>

        <div className="pt-4  ">
          <p className="text-black font-semibold text-[15px] mb-2">
            How would you rate your experience using Quillionz?
          </p>

          <div className="">
            {[1, 2, 3, 4, 5].map((starCount) => (
              <span
                key={starCount}
                className={`text-2xl cursor-pointer ${
                  selectedStars >= starCount ? "text-orange-400" : ""
                }`}
                onClick={() => handleStarClick(starCount)}
              >
                <i className="fa-solid fa-star mx-2"></i>
              </span>
            ))}
            {showRatingError && (
              <p className="text-red-500 mt-2">Please give a rating.</p>
            )}

            <div className=" my-3 py-2 ">
              {selectedStars >= 4 && (
                <div>
                  <p className="text-black font-semibold text-[15px] mb-2">
                    Tell your friends about Quillionz!
                  </p>

                  <div className="flex space-x-2 text-2xl gap-4">
                    <Tooltip
                      title="Facebook"
                      arrow
                      placement="top"
                      TransitionComponent={Zoom}
                      TransitionProps={{ timeout: 300 }}
                    >
                      <a
                        href="https://www.facebook.com"
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        <i className="fa-brands fa-facebook hover:opacity-80 text-[#2660c5]"></i>
                        {""}
                      </a>
                    </Tooltip>

                    <Tooltip
                      title="Twitter"
                      arrow
                      placement="top"
                      TransitionComponent={Zoom}
                      TransitionProps={{ timeout: 300 }}
                    >
                      <a
                        href="https://twitter.com/intent/tweet?url=www.quillionz.com&text=Check%20out%20Quillionz,%20world%E2%80%99s%20first%20AI-powered%20platform%20for%20creating%20questions,%20quizzes,%20and%20assessments%E2%80%94within%20seconds%20and%20completely%20free.%20https://bit.ly/2NRRTTQ%20&hashtags=ArtificialIntelligence%2CQuillionz%2CEdTech%2Cquestioncreation%2Cquizzes%2Cassessment"
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        <i className="fa-brands fa-square-x-twitter"></i>
                        {""}
                      </a>
                    </Tooltip>

                    <Tooltip
                      title="Linkedin"
                      arrow
                      placement="top"
                      TransitionComponent={Zoom}
                      TransitionProps={{ timeout: 300 }}
                    >
                      <a
                        href="https://www.linkedin.com/sharing/share-offsite/?url=www.quillionz.com"
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        <i
                          className="fa-brands fa-linkedin hover:opacity-80 text-[#275bb4]"
                          aria-hidden="true"
                        ></i>
                        {""}
                      </a>
                    </Tooltip>

                    <Tooltip
                      title="Pinterest"
                      arrow
                      placement="top"
                      TransitionComponent={Zoom}
                      TransitionProps={{ timeout: 300 }}
                    >
                      <a
                        href="https://www.pinterest.com"
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        <i className="fa-brands fa-pinterest hover:opacity-80 text-[#f02d2d]"></i>
                        {""}
                      </a>
                    </Tooltip>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="  ">
          <p className="text-black font-semibold text-[15px] mb-2">
            Let us know your thoughts on Quillionz.
          </p>
          <textarea
            className={`w-full resize-none bg-transparent outline-1 border custom-scrollbar overflow-none p-2 ${
              showTextareaError ? "placeholder-red-500" : ""
            }`}
            placeholder="Please type something here..."
            onChange={handleTextareaChange}
          ></textarea>
          {errorMessage && (
          <p className="text-red-500 animate-fade-slide-down text-[13px] signup-error-message w-full border border-red-500  p-2  mb-3">
            {errorMessage}
          </p>
        )}
        </div>

        <div className="flex place-content-center gap-8">
          <Tooltip
            title="Submit your feedback"
            arrow
            placement="top"
            TransitionComponent={Zoom}
            TransitionProps={{ timeout: 300 }}
          >
            <button
              className="mt-4 px-4 py-3 bg-[#f4833d] text-white rounded hover:bg-[#e06a21] "
              onClick={handleFeedbackSubmit}
            >
              Submit
            </button>
          </Tooltip>

          <Tooltip
            title="Share your feedback later"
            arrow
            placement="top"
            TransitionComponent={Zoom}
            TransitionProps={{ timeout: 300 }}
          >
            <button
              className="mt-4 px-4 py-3  text-[#f4833d] rounded hover:bg-[#6c757d] hover:border-[#6c757d] border border-[#f4833d] hover:text-white"
              onClick={handleModalClose}
            >
              Later
            </button>
          </Tooltip>
        </div>

        {/* Loading overlay */}
        {isLoading && <LoadingOverlay />}
      </div>
    </Modal>
  );
};

export default ShareYourFeedbackModal;
